<template>
  <FlashNotifications ref="flash" />

  <div class="flex flex-col h-app-screen bg-gray-50 dark:bg-[#0d1117]">
    <!-- Header -->
    <div class="flex-shrink-0 sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between gap-4 px-4 sm:px-6 h-14 sm:h-auto sm:py-3.5">
        <div class="min-w-0">
          <h1 class="text-[22px] sm:text-xl font-bold tracking-tight leading-none text-gray-900 dark:text-white">Hellobar</h1>
          <p class="hidden sm:block text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            One message shown at the top of the app for every signed in user, above every other reminder.
          </p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button v-if="form" type="button" data-test="preview-on-site" @click="togglePreviewOnSite" :disabled="!hasMessage && !previewOnSite"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="previewOnSite
              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path v-if="!previewOnSite" stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
              <path v-else stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
            {{ previewOnSite ? 'Stop preview' : 'Preview on site' }}
          </button>
          <button type="button" data-test="save-hellobar" @click="save" :disabled="!canSave"
            class="inline-flex items-center gap-1.5 px-4 py-1.5 sm:py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full sm:rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ store.saving ? 'Saving' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        <!-- Loading -->
        <div v-if="loading" class="animate-pulse space-y-6" data-test="hellobar-skeleton">
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl sm:rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 space-y-2">
              <div class="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-3 w-64 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
            <div class="p-6 space-y-5">
              <div class="h-28 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              <div class="flex gap-2">
                <div v-for="n in 7" :key="n" class="w-9 h-9 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
              </div>
              <div class="flex flex-wrap gap-1.5">
                <div v-for="n in 17" :key="n" class="w-9 h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
              <div class="flex items-center justify-between pt-2">
                <div class="space-y-2">
                  <div class="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
                <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl sm:rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div class="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="p-6"><div class="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
          <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
          <button type="button" @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Try again
          </button>
        </div>

        <!-- Loaded -->
        <template v-else-if="form">
          <!-- Content -->
          <section class="bg-white dark:bg-[#1C2128] rounded-2xl sm:rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 class="font-semibold text-gray-900 dark:text-white">Message</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400">Bold, italic, underline and links. Keep it short so it fits on one line on desktop.</p>
            </div>

            <div class="p-6 space-y-6">
              <!-- Rich text -->
              <div>
                <HellobarRichTextInput v-model="form.message" :invalid="errors.length > 0 || overLimit"
                  placeholder="For example: Scheduled maintenance on Friday at 22:00 UTC. Bookings stay open." />
                <div class="mt-1.5 flex items-start justify-between gap-4">
                  <p class="text-xs text-gray-400 dark:text-gray-500">
                    Changing the text or the emoji shows the bar again to people who closed the previous message.
                    Links must start with https:// or /.
                  </p>
                  <span data-test="hellobar-counter" class="flex-shrink-0 text-xs tabular-nums"
                    :class="overLimit
                      ? 'text-red-600 dark:text-red-400 font-semibold'
                      : remaining <= 20 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-400 dark:text-gray-500'">
                    {{ textLength }} / {{ maxLength }}
                  </span>
                </div>
                <ul v-if="errors.length" class="mt-2 space-y-0.5" data-test="hellobar-errors">
                  <li v-for="message in errors" :key="message" class="text-xs text-red-600 dark:text-red-400">{{ message }}</li>
                </ul>
              </div>

              <!-- Background -->
              <div>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">Background</p>
                <div class="flex flex-wrap gap-2" role="radiogroup" aria-label="Background colour">
                  <button v-for="color in colors" :key="color" type="button" role="radio" :aria-checked="form.color === color"
                    :data-test="`hellobar-color-${color}`" @click="form.color = color" :title="COLOR_LABELS[color] || color"
                    class="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-semibold transition-all"
                    :class="form.color === color
                      ? 'border-blue-500 ring-2 ring-blue-500/20 text-gray-900 dark:text-white bg-white dark:bg-gray-800'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'">
                    <span class="w-5 h-5 rounded-full border" :class="SWATCHES[color]"></span>
                    {{ COLOR_LABELS[color] || color }}
                  </button>
                </div>
              </div>

              <!-- Emoji -->
              <div>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">Emoji</p>
                <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Emoji">
                  <button type="button" role="radio" :aria-checked="!form.emoji" data-test="hellobar-emoji-none"
                    @click="form.emoji = null" title="No emoji, show the megaphone icon"
                    class="h-9 px-3 rounded-lg border text-xs font-semibold transition-all"
                    :class="!form.emoji
                      ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'">
                    None
                  </button>
                  <button v-for="emoji in emojis" :key="emoji" type="button" role="radio" :aria-checked="form.emoji === emoji"
                    :data-test="`hellobar-emoji-option`" @click="form.emoji = emoji" :aria-label="`Use ${emoji}`"
                    class="w-9 h-9 rounded-lg border text-lg leading-none flex items-center justify-center transition-all"
                    :class="form.emoji === emoji
                      ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-800'">
                    {{ emoji }}
                  </button>
                </div>
              </div>

              <!-- Active -->
              <div class="flex items-center justify-between gap-4 pt-5 border-t border-gray-100 dark:border-gray-700">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white">Active</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {{ hasMessage ? 'Show this message to every signed in user.' : 'Write a message before turning it on.' }}
                  </p>
                </div>
                <button type="button" role="switch" :aria-checked="form.active" aria-label="Active"
                  data-test="hellobar-active" :disabled="!hasMessage" @click="form.active = !form.active"
                  class="relative inline-flex w-11 h-6 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :class="form.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'">
                  <span class="inline-block w-5 h-5 rounded-full bg-white shadow-sm transition-transform"
                    :class="form.active ? 'translate-x-5' : 'translate-x-0.5'"></span>
                </button>
              </div>
            </div>
          </section>

          <!-- Preview -->
          <section class="bg-white dark:bg-[#1C2128] rounded-2xl sm:rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 class="font-semibold text-gray-900 dark:text-white">Preview</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">How it appears at the top of the app.</p>
              </div>
              <div class="flex items-center gap-2">
                <span data-test="hellobar-status"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  :class="form.active && hasMessage
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'">
                  <span class="w-1.5 h-1.5 rounded-full" :class="form.active && hasMessage ? 'bg-emerald-500' : 'bg-gray-400'"></span>
                  {{ form.active && hasMessage ? 'Live after saving' : 'Hidden' }}
                </span>
                <div class="inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800" role="radiogroup" aria-label="Preview theme">
                  <button v-for="mode in ['light', 'dark']" :key="mode" type="button" role="radio" :aria-checked="previewTheme === mode"
                    :data-test="`preview-theme-${mode}`" @click="previewTheme = mode"
                    class="px-2.5 py-1 rounded-md text-xs font-semibold capitalize transition-colors"
                    :class="previewTheme === mode
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
                    {{ mode }}
                  </button>
                </div>
              </div>
            </div>
            <!-- `.dark` on this wrapper switches every dark: utility inside it,
                 because the dark variant is `&:is(.dark *)`. -->
            <div :class="{ dark: previewTheme === 'dark' }">
              <div class="bg-gray-50 dark:bg-[#0d1117]" :class="{ 'opacity-60': !(form.active && hasMessage) }">
                <HellobarView :bar="previewBar" />
                <div class="px-6 py-4 space-y-2">
                  <div class="h-3 w-40 rounded bg-gray-200 dark:bg-gray-800"></div>
                  <div class="h-3 w-64 rounded bg-gray-100 dark:bg-gray-800/60"></div>
                </div>
              </div>
            </div>
          </section>

          <p v-if="previewOnSite" class="text-xs text-amber-700 dark:text-amber-400 text-center" data-test="preview-on-site-note">
            Previewing on site in your browser only. Edits here update the preview live. Close the bar there or press Stop preview to end it.
          </p>

          <p v-if="store.data.updated_at" class="text-xs text-gray-400 dark:text-gray-500 text-center" data-test="hellobar-last-saved">
            Last saved {{ lastSaved }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useAdminHellobarStore } from 'stores/admin_hellobar.js'
import { savePreviewDraft, clearPreviewDraft, readPreviewDraft } from 'stores/hellobar.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import HellobarView from 'controllers/vue_components/hellobar/HellobarView'
import HellobarRichTextInput from 'controllers/vue_components/hellobar/HellobarRichTextInput'
import { sanitizeHellobarHtml, hellobarPlainText } from 'controllers/vue_components/hellobar/richText.js'

const COLOR_LABELS = {
  blue: 'Blue', green: 'Green', amber: 'Amber', red: 'Red', purple: 'Purple', gray: 'Gray', dark: 'Dark',
}
const SWATCHES = {
  blue: 'bg-blue-100 border-blue-300',
  green: 'bg-emerald-100 border-emerald-300',
  amber: 'bg-amber-100 border-amber-300',
  red: 'bg-red-100 border-red-300',
  purple: 'bg-violet-100 border-violet-300',
  gray: 'bg-gray-200 border-gray-300',
  dark: 'bg-gray-900 border-gray-700',
}

// The page to open for "Preview on site": a real app page with the bar slot.
const PREVIEW_PAGE = '/calendar'

const store = useAdminHellobarStore()
const flash = ref(null)

const loading = computed(() => store.loading)
const error = computed(() => store.error)

// Local copy so editing never touches the cached store state until a save lands.
const form = ref(null)
const errors = ref([])
const previewTheme = ref('light')
const previewOnSite = ref(false)

// The server decides the options, so the page never offers a choice it rejects.
const colors = computed(() => store.data?.colors || Object.keys(COLOR_LABELS))
const emojis = computed(() => store.data?.emojis || [])

const maxLength = computed(() => store.data?.max_length || 180)
const cleanMessage = computed(() => sanitizeHellobarHtml(form.value?.message))
const textLength = computed(() => hellobarPlainText(cleanMessage.value).length)
const remaining = computed(() => maxLength.value - textLength.value)
const overLimit = computed(() => remaining.value < 0)
const hasMessage = computed(() => textLength.value > 0)

const isDirty = computed(() => {
  if (!form.value || !store.data) return false
  return cleanMessage.value !== store.data.message ||
    form.value.active !== store.data.active ||
    form.value.color !== store.data.color ||
    (form.value.emoji || null) !== (store.data.emoji || null)
})
const canSave = computed(() =>
  isDirty.value && !store.saving && !overLimit.value && !(form.value.active && !hasMessage.value))

// Clearing the text switches Active off, mirroring the model rule that an
// active bar needs a message.
watch(hasMessage, (present) => {
  if (!present && form.value) form.value.active = false
})

const previewBar = computed(() => ({
  key: 'preview',
  kind: 'admin',
  tone: form.value?.color || 'blue',
  icon: 'megaphone',
  emoji: form.value?.emoji || null,
  title: null,
  body: 'Your message will appear here.',
  body_html: hasMessage.value ? cleanMessage.value : null,
  cta: null,
  dismissible: true,
}))

// ── Preview on site ──────────────────────────────────────────────────────────
function writeDraft() {
  savePreviewDraft({
    html: cleanMessage.value,
    text: hellobarPlainText(cleanMessage.value),
    color: form.value.color,
    emoji: form.value.emoji || null,
  })
}

function togglePreviewOnSite() {
  if (previewOnSite.value) {
    clearPreviewDraft()
    previewOnSite.value = false
    return
  }
  writeDraft()
  previewOnSite.value = true
  window.open(PREVIEW_PAGE, '_blank', 'noopener')
}

// Keep the open preview tab in step with every edit.
watch(() => form.value && [cleanMessage.value, form.value.color, form.value.emoji], () => {
  if (previewOnSite.value && form.value) writeDraft()
})

const lastSaved = computed(() => {
  const data = store.data
  const when = new Date(data.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  return data.updated_by ? `by ${data.updated_by.full_name} on ${when}` : `on ${when}`
})

function syncForm() {
  if (!store.data) return
  form.value = {
    message: store.data.message,
    active: store.data.active,
    color: store.data.color,
    emoji: store.data.emoji || null,
  }
}

async function save() {
  if (!canSave.value) return
  errors.value = []
  const result = await store.save({
    message: cleanMessage.value,
    active: form.value.active,
    color: form.value.color,
    emoji: form.value.emoji,
  })
  if (result.ok) {
    syncForm()
    flash.value?.show('Hellobar saved', 'notice')
  } else {
    errors.value = result.errors
    flash.value?.show(result.errors[0], 'alert')
  }
}

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
  syncForm()
}

onMounted(async () => {
  // A preview started earlier and still running shows as active here.
  previewOnSite.value = !!readPreviewDraft()
  await store.fetchIfStale()
  syncForm()
})
</script>
